import React from 'react';
import { TFunction } from 'i18next';
import Input from '@lib/components/Input/Input';
import { FieldItem } from '@lib/interfaces/form';
import {
  getCodeValidation,
  getFeatureCollectionValidation,
  getFieldValidation,
  getOptionsArrayValidation,
  getOptionValidation,
} from '@lib/utils/validationUtils';
import SectorFieldNames from '@lib/enums/fieldNames/sectorFieldNames';
import Map from '@lib/components/Map/Map';
import Typography from '@lib/components/Typography/Typography';
import RadioButtonGroup, {
  RadioButtonWrapViews,
} from '@lib/components/RadioButtonGroup/RadioButtonGroup';
import RadioButtonAsChipColor from '@lib/components/RadioButtonAsChipColor/RadioButtonAsChipColor';
import Select, {
  SelectComponents,
  SelectProps,
} from '@lib/components/Select/Select';
import UserOption from '@lib/components/Select/components/UserOption';
import UserSingleValue from '@lib/components/Select/components/UserSingleValue';
import MultiSelectValueContainer from '@lib/components/Select/components/MultiSelectValueContainer';
import { GetLoadOptions } from '@lib/components/Select/types';
import UserChipListField from '@lib/components/UserChipListField/UserChipListField';
import SiteChipsList from '@lib/components/SiteChipListField/SiteChipListField';
import SupportiveMessage, {
  SupportiveMessageColor,
  SupportiveMessageType,
} from '@lib/components/SupportiveMessage/SupportiveMessage';
import formStyles from '@lib/assets/styles/Form.module.scss';
import TextEditor from '@lib/components/TextEditor/TextEditor';
import { MAX_TEXT_EDITOR_FIELD_LENGTH } from '@lib/enums/form';
import getColorOptions from '@lib/components/Chip/utils/colorEnum';

type FormFieldsArgs = {
  assigneeIds: { id: string }[];
  getSitesLoadOptions: GetLoadOptions;
  getUsersLoadOptions: GetLoadOptions;
  getResponsiblePersonLoadOptions: GetLoadOptions;
  sectorId: string | undefined;
  sitesIds: { id: string }[];
  t: TFunction<'translation', undefined>;
};

const geoJsonObj = {
  type: 'FeatureCollection',
  features: [],
};

export const defaultValues = {
  [SectorFieldNames.Id]: undefined,
  [SectorFieldNames.DetailsTitle]: undefined,
  [SectorFieldNames.Name]: '',
  [SectorFieldNames.Code]: '',
  [SectorFieldNames.Description]: '',
  [SectorFieldNames.Color]: undefined,
  [SectorFieldNames.LocationTitle]: undefined,
  [SectorFieldNames.LocationMessage]: undefined,
  [SectorFieldNames.LocationGeojson]: geoJsonObj,
  [SectorFieldNames.AssignationsTitle]: undefined,
  [SectorFieldNames.UserId]: undefined,
  [SectorFieldNames.Sites]: undefined,
  [SectorFieldNames.SiteList]: undefined,
  [SectorFieldNames.UserMembershipList]: undefined,
  [SectorFieldNames.UserMemberships]: undefined,
};

export default function getSectorFormFields({
  assigneeIds,
  getSitesLoadOptions,
  getUsersLoadOptions,
  getResponsiblePersonLoadOptions,
  sectorId,
  sitesIds,
  t,
}: FormFieldsArgs): FieldItem[] {
  return [
    {
      name: SectorFieldNames.DetailsTitle,
      customRender: () => (
        <Typography variant="h3" key="details">
          {t('details')}
        </Typography>
      ),
    },
    {
      name: SectorFieldNames.Name,
      label: t('sector-name'),
      element: Input,
      validation: getFieldValidation({ t, required: true }),
    },
    {
      name: SectorFieldNames.Code,
      label: t('code'),
      element: Input,
      validation: getCodeValidation({ t, max: 10 }),
    },
    {
      name: SectorFieldNames.Description,
      label: t('description'),
      element: TextEditor,
      validation: getFieldValidation({
        maxLength: MAX_TEXT_EDITOR_FIELD_LENGTH,
        t,
      }),
    },
    {
      name: SectorFieldNames.Color,
      label: t('label-color'),
      element: RadioButtonGroup,
      validation: getOptionValidation({ required: true, t }),
      componentProps: {
        options: getColorOptions({ t }),
        RadioButtonComponent: RadioButtonAsChipColor,
        wrapView: RadioButtonWrapViews.colors,
      },
    },
    {
      name: SectorFieldNames.LocationTitle,
      customRender: () => (
        <Typography
          variant="h3"
          key="location"
          className={formStyles.blockStart}
        >
          {t('location-on-map')}
        </Typography>
      ),
    },
    {
      name: SectorFieldNames.LocationMessage,
      customRender: () => (
        <SupportiveMessage
          text={t('sector-map-desc')}
          color={SupportiveMessageColor.Blue}
          leftIcon="info"
          type={SupportiveMessageType.FilledOnly}
          leftIconSymbolsOutlined
          hasBottomSpace={false}
        />
      ),
    },
    {
      name: SectorFieldNames.LocationGeojson,
      element: Map,
      validation: getFeatureCollectionValidation({ required: true, t }),
      componentProps: {
        maxPolygons: 1,
      },
    },
    {
      name: SectorFieldNames.AssignationsTitle,
      customRender: () => (
        <Typography
          variant="h3"
          key="assignations"
          className={formStyles.blockStart}
        >
          {t('configure-assignations')}
        </Typography>
      ),
    },
    {
      name: SectorFieldNames.UserId,
      label: t('responsible-person'),
      element: Select,
      validation: getOptionValidation({ t }),
      componentProps: {
        isAsync: true,
        getLoadOptions: getResponsiblePersonLoadOptions,
        defaultOptions: false,
        components: {
          Option: UserOption,
          SingleValue: UserSingleValue,
        } as unknown as SelectComponents,
      },
    },
    {
      name: SectorFieldNames.Sites,
      label: t('assign-sites-to-sector'),
      element: Select,
      validation: getOptionsArrayValidation({ t }),
      componentProps: {
        isAsync: true,
        isMulti: true,
        getLoadOptions: getSitesLoadOptions,
        defaultOptions: false,
        menuPlacement: 'top',
        components: {
          ValueContainer: MultiSelectValueContainer,
        } as unknown as SelectComponents,
      } as SelectProps,
      hideErrorsSpace: true,
    },
    {
      name: SectorFieldNames.SiteList,
      element: SiteChipsList,
      componentProps: {
        fieldName: SectorFieldNames.Sites,
        sectorId,
        ids: sitesIds,
      },
      isUseWatch: true,
    },
    {
      name: SectorFieldNames.UserMemberships,
      label: t('assign-users-to-sector'),
      element: Select,
      validation: getOptionsArrayValidation({ t }),
      componentProps: {
        isAsync: true,
        isMulti: true,
        getLoadOptions: getUsersLoadOptions,
        defaultOptions: false,
        menuPlacement: 'top',
        components: {
          Option: UserOption,
          ValueContainer: MultiSelectValueContainer,
        } as unknown as SelectComponents,
      } as SelectProps,
      hideErrorsSpace: true,
      description: t('full-admins-not-listed'),
    },
    {
      name: SectorFieldNames.UserMembershipList,
      element: UserChipListField,
      componentProps: {
        fieldName: SectorFieldNames.UserMemberships,
        sectorId,
        ids: assigneeIds,
      },
      isUseWatch: true,
    },
  ];
}
